import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { formatDate } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import type { TableSort } from '../../../lib/useTableSort'
import { MY_ACTIONS_PAGE_SIZES } from '../hooks/useMyReportActions'
import { playClickSound } from '../../../lib/sound'
import type { DashboardActionRow } from '../types'
import DashboardAssigneeCell from './DashboardAssigneeCell'
import './MyPendingActionsCard.css'
import { useCurrentUser } from '../../../auth/useCurrentUser'

interface Props {
  rows: DashboardActionRow[]
  loading: boolean
  /** Toplam kayıt (sunucudan) — sayfalayıcı bunu gösterir. */
  total: number
  /** 0 tabanlı sayfa numarası. */
  page: number
  onPageChange: (page: number) => void
  /** Satır/sayfa (mockup'taki "5 / sayfa" seçicisi). */
  pageSize: number
  onPageSizeChange: (size: number) => void
  /** Kolon sıralaması (`useTableSort`) — sunucu-taraflı; varsayılanı hedef teslim tarihi ↑'dir. */
  sorting: TableSort
  /**
   * Hangi süzgeçle daraldığını söyleyen tek satır; süzgeç varsayılandaysa bölümün kendi tanımı yazar.
   * Metni **çağıran** üretir (seçim adları grafiklerle aynı kaynaktan gelmeli).
   */
  note: string
  /**
   * "Önceliklendir" — satırdaki aksiyonun sorumlusuna bildirim gönderir (§7.4). Verilmezse buton ve
   * kolonu **hiç çizilmez** (`DASHBOARD_PRIORITIZE` yetkisi yoksa — {@code useCanPrioritize}).
   */
  onPrioritize?: (row: DashboardActionRow) => void
  /** Bir istek sürüyor mu? (butonlar pasifleşir — çift gönderim olmasın) */
  prioritizing: boolean
}

/**
 * Raporum'un **"Aksiyon Beklenen Maddeler"** tablosu (Raporum revizyonu §7, 2026-08-19).
 *
 * <p><b>Bir özet değil, çalışma listesidir</b> — bu yüzden Raporum'un diğer bloklarının kuralından
 * (sabit ≤5 satır, kaydırma/sayfalama yok) <b>bilinçle ayrılır</b>: sunucu-taraflı sayfalanır, kolonlardan
 * sıralanabilir ve her satırdan işlem yapılabilir. Analiz de bu bölümde açıkça "Tümünü Gör bağlantısı
 * bulunmayacaktır, sayfalama yapısı kullanılacaktır" diyor.
 *
 * <p><b>Panonun dört grafiği bu tabloyu daraltır</b> (§1.3/§2.3/§3.3/§4.4): seçim URL'de durur, başlığın
 * altındaki not hangi kümenin gösterildiğini yazar.
 *
 * <p><b>Yönetici Raporu'nun alt tablosundan iki farkı</b> (kolonlar mockup'tan): "Gecikme Süresi" kolonu
 * <b>yoktur</b> (bu bölüm gecikmeyi değil bekleyen işi listeler; gecikme kendi bölümünde durur) ve
 * "Tüm Aksiyonları Önceliklendir" başlık butonu <b>yoktur</b> — analiz yalnız satır bazlı işlem tanımlıyor.
 *
 * <p><b>Boş hücre kuralı</b> (053/FR-011c): değeri olmayan alan <b>boş</b> bırakılır, {@code -}/{@code —}
 * yazılmaz. "Önceliklendir" butonundaki tıklama satıra sızmaz — aksi hâlde her işlem kullanıcıyı aynı
 * anda detay sayfasına atardı. <b>İki dalda iki ayrı yolla</b> (15.09.2026): masaüstü {@code DataTable}
 * dalında hâlâ {@code stopPropagation}'dır (satırın {@code onRowClick}'i canlı), kart dalında ise
 * <b>yapısaldır</b> — kart kökünde handler yoktur, düğme başlık kontrolünün kardeşidir (overlay-link,
 * anayasa → kart kuralı).
 */
export default function MyPendingActionsCard({
  rows,
  loading,
  total,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  sorting,
  note,
  onPrioritize,
  prioritizing,
}: Readonly<Props>) {
  const { t } = useTranslation()
  /** 129 — IT Admin maskesinde başlık/açıklama `***` gelir; sıralama oku çizilmez. */
  const contentMasked = useCurrentUser().data?.actionContentMasked === true
  // Detaya giderken anasayfa origin olarak taşınır → Aksiyon Detay'da "Geri" anasayfaya döner.
  const navigate = useNavigateWithOrigin()
  // Telefon/tablet genişliğinde tablo yerine kart listesi — bu kartın kendi `MyPendingActionsCard.css`
  // sınıfları vardır (paylaşılan `ui-card*` KULLANILMAZ; bkz. o dosyanın üst notu).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const goToDetail = (id: number) => navigate(`/minutes/actions/${id}`)

  return (
    <SectionCard title={t('home.actions.title')} className="home-crit">
      <p className="home-actions-note">{note}</p>

      {isMobile ? (
        <div className="home-pending-cards">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="home-pending-card home-pending-card--skeleton" aria-hidden />
            ))}
          {!loading && rows.length === 0 && (
            <EmptyState
              icon="pi pi-check-square"
              title={t('home.my.pending.emptyTitle')}
              description={t('home.my.pending.emptyDesc')}
            />
          )}
          {!loading &&
            rows.map((row) => (
              // 15.09.2026 — **overlay-link**: kart kökü handler'sız, detayı başlıktaki gerçek
              // `<button>` açıyor ve kartı `::after` ile kaplıyor (`ui.css` → `.ui-card--linked`/
              // `.ui-card-link`; kartın görünümü `.home-pending-card`ta kalır). "Önceliklendir" o
              // kontrolün KARDEŞİdir. Önceki biçimde kart `role="button"` taşıyor, düğme onun İÇİNDE
              // kalıyordu; ARIA'da interaktif kontrol başka bir interaktif kontrol içeremez (THY
              // inceleme bulgusu, 15.09.2026; anayasa → kart kuralı). Buradaki eski `onKeyDown` ayrıca
              // `e.target === e.currentTarget` denetimi bile TAŞIMIYORDU: düğmede Enter'a basmak hem
              // önceliklendirip hem detaya götürüyordu.
              <div key={row.id} className="home-pending-card ui-card--linked">
                <div className="home-pending-card-actions">
                  <span className="home-pending-card-title">
                    <button type="button" className="ui-card-link" onClick={() => goToDetail(row.id)}>
                      {row.name}
                    </button>
                  </span>
                  {onPrioritize && (
                    <Button
                      type="button"
                      outlined
                      className="home-remind"
                      label={t('home.remind.one')}
                      disabled={prioritizing}
                      onClick={() => {
                        // `stopPropagation` KALDIRILDI: kart kökünde artık handler yok, kabarcıklanacak
                        // bir ata kalmadı (bkz. yukarıdaki overlay-link notu).
                        playClickSound()
                        onPrioritize(row)
                      }}
                    />
                  )}
                </div>
                <div className="home-pending-card-foot">
                  <DashboardAssigneeCell assignees={row.assignees} />
                  {row.deadline && (
                    <span className="home-pending-card-deadline">{formatDate(row.deadline)}</span>
                  )}
                </div>
              </div>
            ))}
          {!loading && total > 0 && (
            <Paginator
              first={page * pageSize}
              rows={pageSize}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                const nextSize = e.rows ?? pageSize
                if (nextSize !== pageSize) {
                  onPageSizeChange(nextSize)
                  return
                }
                onPageChange(Math.floor((e.first ?? 0) / nextSize))
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="home-pending-cards-paginator"
            />
          )}
        </div>
      ) : (
        <DataTable
          value={rows}
          loading={loading}
          lazy
          paginator
          first={page * pageSize}
          rows={pageSize}
          totalRecords={total}
          rowsPerPageOptions={MY_ACTIONS_PAGE_SIZES}
          onPage={(e) => {
            const nextSize = e.rows ?? pageSize
            if (nextSize !== pageSize) {
              // Satır/sayfa değişince BAŞA dönülür: 3. sayfanın ilk kaydı yeni boyda başka sayfaya düşer.
              onPageSizeChange(nextSize)
              return
            }
            onPageChange(Math.floor((e.first ?? 0) / nextSize))
          }}
          sortField={sorting.sortField}
          sortOrder={sorting.sortOrder}
          onSort={sorting.onSort}
          // Sıralama daima bir kolondadır (varsayılan hedef teslim tarihi ↑): "sıralamasız" hâl kullanıcının
          // bilerek seçeceği bir durum değil, yalnız kafa karıştıran üçüncü bir tıklama olurdu.
          removableSort={false}
          onRowClick={(e) => navigate(`/minutes/actions/${(e.data as DashboardActionRow).id}`)}
          rowClassName={() => 'mtg-row-clickable'}
          emptyMessage={
            <EmptyState
              icon="pi pi-check-square"
              title={t('home.my.pending.emptyTitle')}
              description={t('home.my.pending.emptyDesc')}
            />
          }
        >
          {/* Başlık hücresi SARAR — uzun bir başlık tabloyu yana kaydırmasın (`tk-cell-wrap` global
              değiştiricidir; `home-actions-name` yalnız bu kolonun ölçülerini taşır). */}
          <Column
            field="name"
            sortable={!contentMasked}
            header={t('home.my.pending.columns.name')}
            bodyClassName="tk-cell-strong tk-cell-wrap home-actions-name"
          />
          <Column
            field="assignee"
            sortable
            header={t('home.my.pending.columns.assignee')}
            body={(row: DashboardActionRow) => <DashboardAssigneeCell assignees={row.assignees} />}
          />
          <Column
            field="deadline"
            sortable
            header={t('home.my.pending.columns.deadline')}
            body={(row: DashboardActionRow) => (row.deadline ? formatDate(row.deadline) : '')}
          />
          {/* Kolon da gizlenir (yalnız buton değil): boş başlıklı bir kolon tabloda yer kaplardı. */}
          {onPrioritize && (
            <Column
              header={t('home.my.pending.columns.prioritize')}
              body={(row: DashboardActionRow) => (
                <Button
                  type="button"
                  outlined
                  className="home-remind"
                  label={t('home.remind.one')}
                  disabled={prioritizing}
                  onClick={(e) => {
                    // Satır tıklaması detaya götürür; önceliklendirme onu tetiklememeli.
                    e.stopPropagation()
                    playClickSound()
                    onPrioritize(row)
                  }}
                />
              )}
            />
          )}
        </DataTable>
      )}
    </SectionCard>
  )
}

